import objStr from 'obj-str';
import React, { useRef, useState } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { Portal } from '@/components/Portal';
import { useBrowserLayoutEffect } from '@/hooks/useBrowserLayoutEffect';
import { useOverflowHidden } from '@/hooks/useOverflowHidden';
import { useClickOutside } from '@/hooks/useClickOutside';
import styles from './SlidePaneSmall.module.scss';

interface ISlidePane {
  buttonLabel: string;
  children: React.ReactNode;
  title?: string;
  isOpen?: boolean;
  side?: 'left' | 'right';
  size?: 'auto';
  animationTime?: number;
  closeSlidepane: AppCallbackFn;
  toggleSlidepane: AppCallbackFn;
}

interface RestProps {
  [x: string]: any;
}

export const SlidePaneSmall = ({
  title,
  buttonLabel,
  children,
  isOpen,
  closeSlidepane,
  toggleSlidepane,
  side = 'left',
  ...restProps
}: ISlidePane & RestProps) => {
  const paneRef = useRef(null);
  const [isSlidepaneOpen, setIsSlidePaneOpen] = useState<boolean>(isOpen ?? false);

  const togglePane = () => {
    toggleSlidepane();
  };

  const closePane = () => {
    closeSlidepane();
  };

  useOverflowHidden(isSlidepaneOpen);
  useClickOutside(paneRef, closePane);

  useBrowserLayoutEffect(() => {
    setIsSlidePaneOpen(() => isOpen ?? false);
  }, [isOpen]);

  const slidePaneClassNames = objStr({
    [styles.SlidePaneSmall]: true,
    [styles.SlidePaneLeft]: side === 'left',
    [styles.SlidePaneRight]: side === 'right',
    [styles.SlidePaneIsOpen]: isOpen,
    [restProps?.openClassName]: isOpen && !!restProps.openClassName,
    [restProps?.className]: !!restProps?.className
  });

  const toggleButtonClassNames = objStr({
    [styles.toggleButton]: true,
    [styles.toggleLeft]: side === 'left',
    [styles.toggleRight]: side === 'right'
  });

  return (
    <>
      <Portal selector="#portal">
        <div className={slidePaneClassNames} ref={paneRef}>
          {title && (
            <Row>
              <Col>
                <h1 className="h1 my-2 ml-2">{title}</h1>
              </Col>
              <Col xs="auto">
                <button className={`${styles.btnClose} btn btn-link close`} onClick={closePane}>
                  <span aria-hidden="true">×</span>
                </button>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <div className={styles.content}>{children}</div>
              <Button variant="primary" className={toggleButtonClassNames} onClick={togglePane}>
                <span>{buttonLabel}</span>
              </Button>
            </Col>
          </Row>
        </div>
      </Portal>

      {isSlidepaneOpen && (
        <Portal selector="body">
          <div className={`${styles.backdrop} ${isOpen ? styles.fadeIn : styles.fadeOut}`}></div>
        </Portal>
      )}
    </>
  );
};
